
type handler = (...args: unknown[]) => unknown;

/**
 * A SetMap extends the standard Map class with an add method so that its values
 * are always a Set. This is useful for grouping objects under a common key.
 */
class SetMap<K, V> extends Map<K, Set<V>> {

    /**
     * Adds a value into the Set instance that is stored under the given key. If
     * this is the first value associated with this key, then the underlying Set
     * is created.
     * @param key - the key object; since this is a Map it can be any type
     * @param value - the stored item
     * @returns - returns the map object
     */
    add(key: K, value: V) {
        if(this.has(key)) {
            let s: Set<V> = this.get(key);
            s.add(value);
        } else {
            let s: Set<V> = new Set();
            s.add(value);
            this.set(key, s);
        }
        return this;
    }

    /**
     * Deletes the given value from the underlying set. Note that this will not
     * leave an empty set under the given key, so the `has` method will
     * correctly tell you whether all the entries under the key have been
     * removed.
     * @param key - the key object; since this is a Map it can be any type
     * @param value - the stored item
     * @returns - true if the item was an element of the set under the
     * given key, false otherwise.
     */
    deleteValue(key: K, value: V) {
        if(this.has(key)) {
            let s = this.get(key);
            var result = s.delete(value);
            if(s.size == 0)
                super.delete(key);
            return result;
        } else {
            return false;
        }
    }

    /**
     * Create copies of the Map and all of its contained Sets. This does not
     * copy any of the keys or values contained therein.
     * @returns A new SetMap containing all of the same key and value
     * references.
     */
    clone() {
        const result = new SetMap();
        for(let key of this.keys()) {
            let value = new Set(this.get(key));
            result.set(key, value);
        }
        return result;
    }

    /**
     * Generates the revesre mapping such that each value becomes a key and
     * each key becomes a value.
     */
    reverse(): SetMap<V, K> {
        const result: SetMap<V, K> = new SetMap();
        for(let [m, ns] of this.entries()){
            for(let n of ns) {
                result.add(n, m);
            }
        }
        return result;
    }

}

/**
 * Like the browser's EventDispatcher interface, this EventDispatcher mixin (a
 * class factory function), allows classes to simply and easily handle routing
 * events.
 */
export default class EventDispatcher {

    private handlers: SetMap<string, handler> = new SetMap();


    on(type: string, handler: handler) {
        this.handlers.add(type, handler);
    }

    once(type: string, handler: handler) {
        const wrapper = () => {
            handler();
            this.off(type, wrapper);
        }
        this.on(type, wrapper);
    }

    when(type: string) {
        return new Promise((resolve, _reject) => {
            this.once(type, resolve);
        })
    }

    off(type: string, handler?: handler) {
        if(!handler == null){
            this.handlers.delete(type);
        } else {
            this.handlers.deleteValue(type, handler);
        }
    }

    alloff() {
        this.handlers = new SetMap();
    }

    trigger(type: string, ...args: unknown[]) {
        let queue = this.handlers.get(type);
        if(queue){
            let copy: Array<handler> = Array.from(queue);
            for(let handler of copy){
                try {
                    handler(...args);
                } catch(error) {
                    console.error(error);
                }
            }
        }
    }

    /**
     * Making a batch update to an event dispatcher suspends triggering
     * until after the batch is complete. This is emphatically not thread
     * safe, but it's JavaScript, so the EventDispatcher instance should
     * always be thread local. If that ever changes... woe betide.
     */
    batch(fn: () => void) {
        let events = new Array();
        // adds a trigger implementation to this instance only
        this.trigger = (type, ...args) => {
            events.push({type, args});
        }

        fn();

        delete this.trigger; // fall back to the prototype implementation
        for(let {type, args} of events) {
            this.trigger(type, ...args);
        }
    }

    /**
     * Binding registers an event handler for a given type of event and
     * executes that handler immediately. Thus, binding is equivalent to
     * on(..., nowandlater(fn)), where fn is the handler function you'd pass
     * to the on method.
     */
    bind(property: string, handler: handler) {
        this.on(property, handler);
        handler();
    }
}
